import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ProviderNoteStateEnum } from 'src/app/enum/provider-note-state-enum';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ProviderNoteRetentionModel } from 'src/app/models/shopping/provider-note-retention.model';
import { ProviderNoteModel } from 'src/app/models/shopping/provider-note.model';
import { ProviderNoteRetentionTableModel } from 'src/app/models/tables/shopping/provider-note-retention-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { ProviderNoteRetentionService } from 'src/app/services/shopping/provider-note-retention.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-list-provider-note-retention',
  templateUrl: './list-provider-note-retention.component.html',
  styleUrls: ['./list-provider-note-retention.component.scss'],
  standalone: false
})
export class ListProviderNoteRetentionComponent implements OnInit {

  public readonly config = inject(DynamicDialogConfig);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly formControlStateService = inject(FormControlStateService);
  private readonly providerNoteRetentionService = inject(ProviderNoteRetentionService);
  private readonly ref = inject(DynamicDialogRef);
  private readonly toastHandlerService = inject(ToastHandlerService);

  ascendant: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  data: PagedResponseModel<ProviderNoteRetentionTableModel> = new PagedResponseModel<ProviderNoteRetentionTableModel>();
  disabledActions: boolean = false;
  formProviderNoteRetention: FormGroup = new FormGroup({});
  header: ColumnResponseModel[] = [];
  limit: number = 10;
  listTaxes: any[] = [];
  page: number = 1;
  permissionUpdatable: boolean = false;
  providerNoteModel: ProviderNoteModel = new ProviderNoteModel();
  providerNoteRetentionModel: ProviderNoteRetentionModel = new ProviderNoteRetentionModel();
  suscription: Subscription;
  tableActions: TableAction[] = [];
  taxType: string = '';
  totalValue: number = 0;
  updateProviderNote: boolean = false;

  async ngOnInit(): Promise<void> {
    this.validateForm(new ProviderNoteRetentionModel());
    await this.getColumns();
    await this.getListTaxes();

    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }

    this.suscription = input$?.subscribe((inputs: any) => {
      this.providerNoteModel = inputs.providerNoteModel;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.disabledActions = inputs.disabledForm;
    });

    await this.getProviderNoteRetentionsByProviderNoteIdWithPagination();
    this.tableActions = this.getDefaultActions();

    if (this.disabledActions) {
      this.formProviderNoteRetention.disable();
    } else {
      this.formProviderNoteRetention.enable();
      this.formControlStateService.setControlState(this.formProviderNoteRetention, ['taxRate'], false);
    }
  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  async cancel() {
    this.providerNoteRetentionModel = new ProviderNoteRetentionModel();
    this.validateForm(this.providerNoteRetentionModel);
    this.taxType = '';
    this.totalValue = 0;
  }

  changeBaseValue(baseValue: number) {
    const taxRate = this.formProviderNoteRetention.get('taxRate')?.value ?? 0;
    const taxId = this.formProviderNoteRetention.get('taxId')?.value ?? 0;
    const taxType = this.listTaxes.find(x => x.value === taxId)?.taxType ?? '';
    this.totalValue = taxType === 'ICA' ? (baseValue) * (taxRate) / 1000 : (baseValue) * (taxRate) / 100;
  }

  changeTax(taxId: number) {
    //Buscar el impuesto y traerlo si existe para evitar duplicados
    //tener en cuenta los que se importan automaticamente desde el detalle
    const tax = this.listTaxes.find(x => x.value === taxId);
    const taxType = this.listTaxes.find(x => x.value === taxId)?.taxType ?? '';
    const baseValue = this.providerNoteRetentionModel.providerNoteRetentionId === 0 ?
      this.providerNoteModel?.subtotalValue ?? 0 : this.providerNoteRetentionModel.baseValue;
    this.taxType = taxType;
    this.formProviderNoteRetention.get('taxRate')?.patchValue(tax?.rate ?? 0);
    this.formProviderNoteRetention.get('baseValue')?.patchValue(baseValue);
    this.totalValue = this.taxType === 'ICA' ? (this.formProviderNoteRetention.get('baseValue')?.value ?? 0) * (this.formProviderNoteRetention.get('taxRate')?.value ?? 0) / 1000 :
      (this.formProviderNoteRetention.get('baseValue')?.value ?? 0) * (this.formProviderNoteRetention.get('taxRate')?.value ?? 0) / 100;
  }

  close() {
    const data: ModalObjectType = {};
    data.params = { updateProviderNote: this.updateProviderNote };
    this.ref.close(data);
  }

  async deleteDetail(selectedIds: number[]) {
    const accepted = await this.confirmDialogService.confirm(`¿Realmente desea eliminar el detalle seleccionado?`);
    if (accepted) {
      try {
        await firstValueFrom(this.providerNoteRetentionService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast('success', 'El detalle fue eliminado exitosamente.');
        this.getProviderNoteRetentionsByProviderNoteIdWithPagination();
      } catch (error) {
        console.error('Error al eliminar el detalle de la retención.', error);
      }
    }
  }

  async getColumns() {
    try {
      this.header = await firstValueFrom(this.providerNoteRetentionService.getColumnsForProviderNoteRetentionAsync());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    if (this.providerNoteModel.state == ProviderNoteStateEnum.open) {
      return [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        }
      ];
    } else {
      return [];
    }
  }

  async getListTaxes() {
    try {
      this.listTaxes = await firstValueFrom(this.providerNoteRetentionService.getAllTaxesForSelectAsync());
    } catch (error) {
      console.error('Error al obtener la lista de impuestos.', error);
    }
  }

  async getProviderNoteRetentionsByProviderNoteIdWithPagination() {
    try {
      this.data = await firstValueFrom(
        this.providerNoteRetentionService.getProviderNoteRetentionsByProviderNoteIdWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.providerNoteModel.providerNoteId
        ));
      this.clearSelectionTrigger++;
    } catch (error) {
      console.error('Error al buscar las retenciones.', error);
    }
  }

  async onAction(event: { action: string, item?: ProviderNoteRetentionTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      try {
        if (!event.item.swManual) {
          this.toastHandlerService.showToast('warning', `No se puede editar una retención automatica.`);
          return;
        }
        this.providerNoteRetentionModel = await firstValueFrom(
          this.providerNoteRetentionService.getProviderNoteRetentionById(event.item.id)
        );
        this.validateForm(this.providerNoteRetentionModel);
        this.changeTax(this.providerNoteRetentionModel.taxId);
      } catch (error) {
        console.error('Error al editar la retención:', error);
      }
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteDetail(event.selectedItems);
      this.cancel();
      this.updateProviderNote = true;
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getProviderNoteRetentionsByProviderNoteIdWithPagination();
  }

  setForm(): ProviderNoteRetentionModel {
    this.providerNoteRetentionModel.providerNoteId = this.providerNoteModel.providerNoteId;
    this.providerNoteRetentionModel.taxId = this.formProviderNoteRetention.get("taxId")?.value;
    this.providerNoteRetentionModel.baseValue = this.formProviderNoteRetention.get("baseValue")?.value;
    this.providerNoteRetentionModel.taxRate = this.formProviderNoteRetention.get("taxRate")?.value;
    this.providerNoteRetentionModel.swManual = true;
    return this.providerNoteRetentionModel;
  }

  async send() {
    Object.keys(this.formProviderNoteRetention.controls).forEach((key) => {
      const control = this.formProviderNoteRetention.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formProviderNoteRetention.valid) {
      const modelProviderNoteRetention = this.setForm();
      const isNew = modelProviderNoteRetention.providerNoteRetentionId === 0;
      try {
        await firstValueFrom(isNew
          ? this.providerNoteRetentionService.createProviderNoteRetention(modelProviderNoteRetention)
          : this.providerNoteRetentionService.updateProviderNoteRetention(modelProviderNoteRetention));
        this.getProviderNoteRetentionsByProviderNoteIdWithPagination();
        this.updateProviderNote = true;
        this.toastHandlerService.showToast('success', `La retención se ${isNew ? 'creó' : 'modificó'} exitosamente.`);
        this.cancel();
      } catch (error) {
        console.error('Error al guardar la retención', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      taxId: {
        required: 'Debe seleccionar un impuesto'
      },
      baseValue: {
        required: 'Debe ingresar la base',
        min: 'Debe ingresar una base mayor a 0'
      },
      taxRate: {
        required: 'Debe ingresar el porcentaje',
        min: 'Debe ingresar un porcentaje igual o mayor a 0',
        max: 'El porcentaje no puede ser superior a 100'
      }
    };
    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formProviderNoteRetention.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(providerNoteRetention: ProviderNoteRetentionModel) {
    this.formProviderNoteRetention = this.formBuilder.group({
      taxId: [providerNoteRetention.taxId || null, [Validators.required]],
      baseValue: [providerNoteRetention.baseValue, [Validators.required, Validators.min(1)]],
      taxRate: [providerNoteRetention.taxRate, [Validators.required, Validators.min(0), Validators.max(100)]],
    });

    this.formControlStateService.setControlState(this.formProviderNoteRetention, ['taxRate'], false);
  }

}
